/**
 * Files a supplier's reply as a Supplier Quotation without anyone opening it.
 *
 * ---- Why ----
 *
 * A supplier answers the RFQ with our own workbook filled in. Until now those prices sat in the
 * mail until a purchaser clicked the supplier, read the docked panel and pressed a button — per
 * supplier, per enquiry. The board meanwhile reported the RFQ as unanswered, because "did they
 * come back?" is measured by whether a `Supplier Quotation` exists.
 *
 * The workbook is ALREADY downloaded and parsed for every reply row on screen (the row shows its
 * line count and total), so filing it costs one write and no extra reading.
 *
 * ---- What it will not do ----
 *
 * `buildQuotationFromWorkbook` refuses in four cases and each refusal is respected in full: a
 * workbook belonging to a different RFQ, no prices filled in, a priced line with no item code, or
 * no currency. None is retried and none is guessed at — an unfiled reply stays exactly as it was,
 * openable and fileable by hand.
 *
 * It never touches a reply whose quotation already exists, and never files the same reply twice:
 * `filed` is marked BEFORE the write, so the re-render each write causes cannot re-enter.
 *
 * Renders nothing.
 */

import { useEffect, useRef } from 'react'
import { useParsedOffer } from './ReplyOffer'
import { buildQuotationFromWorkbook, toTransactionDate } from '../../lib/supplierQuotationFromWorkbook'
import type { BuildQuotationResult } from '../../lib/supplierQuotationFromWorkbook'
import type { EnquiryItem } from '../../lib/types'
import type { RfqReply } from '../../lib/api/useRfqReplies'
import type { Rfq } from '../../lib/types'

export function AutoFileReply({
  rfq,
  reply,
  enquiryId,
  company,
  fallbackCurrency,
  enabled,
  enquiryLines,
  onFile,
}: {
  rfq: Rfq
  /** The newest inbound mail for this RFQ. */
  reply: RfqReply
  enquiryId: string
  company: string
  fallbackCurrency: string
  /** False while a manual file/parse is in flight, so the two cannot race for one reply. */
  enabled: boolean
  /** For each filed row's description — see `BuildQuotationInput.enquiryLines`. */
  enquiryLines: readonly EnquiryItem[]
  onFile: (communication: string, result: BuildQuotationResult) => void
}) {
  // Already RFQ-verified: `useParsedOffer` returns null for a workbook belonging to another RFQ,
  // so those prices can never be filed against this one.
  const offer = useParsedOffer(reply.id, rfq.id)
  const filed = useRef<string | null>(null)
  const latest = useRef(onFile)
  useEffect(() => {
    latest.current = onFile
  })

  useEffect(() => {
    if (!enabled || !offer) return
    // Marked BEFORE the write — the write re-renders this row, and without it that re-entry
    // would file the same reply again.
    if (filed.current === reply.id) return
    filed.current = reply.id

    const built = buildQuotationFromWorkbook({
      offer,
      rfq,
      enquiryId,
      company,
      // THEIR date — the day the reply arrived, not today. A quote filed against the wrong day
      // ages wrongly in the comparison.
      transactionDate: toTransactionDate(reply.received_at) ?? new Date().toISOString().slice(0, 10),
      fallbackCurrency,
      enquiryLines,
    })
    /*
      A refusal is NOT reported here. `buildQuotationFromWorkbook`'s four refusals are all things
      a human must judge — a blank workbook, a missing item code — and a toast per unfilable reply
      on an enquiry with a dozen suppliers is noise that trains people to ignore it. The reply
      stays exactly as it was: visible in the table, openable, fileable by hand.
    */
    if (built.ok) latest.current(reply.id, built)
  }, [offer, enabled, reply.id, reply.received_at, rfq, enquiryId, company, fallbackCurrency, enquiryLines])

  return null
}
